import React, { useMemo } from 'react';
import { IRiskTolerance } from '../../../../sp-types/accountTypes';
import { FormatToShortPriceTemplate } from '../../../../utils/FormatValue';

export interface LinearGaugeAdditionalArrowsDataType {
  img: string;
  value: number;
  bigArrow?: boolean;
}

export interface LinearGaugeProps {
  currentValue: number;
  riskTolerance: IRiskTolerance;
  hightInRem?: number;
  tickerHight?: number;
  isRounded?: boolean;
  colors?: [string, string, string];
  numberOfTicks?: 2 | 4 | 8 | 10 | 12;
  additionalArrows?: LinearGaugeAdditionalArrowsDataType[];
  isTicksByColors?: boolean;
  isZoomExposure?: boolean;
  formulaColor?: string;
}

// Zone boundaries as percentages (equal-width zones)
// [0-20%] danger-left  [20-40%] warning-left  [40-60%] stable  [60-80%] warning-right  [80-100%] danger-right
const ZONE_WIDTH = 20;
const ZONES = [
  { start: 0,  end: 20, id: 'danger-left',  color: '#fee2e2', activeColor: '#fca5a5' },
  { start: 20, end: 40, id: 'warning-left', color: '#fef3c7', activeColor: '#fcd34d' },
  { start: 40, end: 60, id: 'stable',       color: '#d1fae5', activeColor: '#6ee7b7' },
  { start: 60, end: 80, id: 'warning-right',color: '#fef3c7', activeColor: '#fcd34d' },
  { start: 80, end: 100,id: 'danger-right', color: '#fee2e2', activeColor: '#fca5a5' },
];

type ZoneId = 'danger-left' | 'warning-left' | 'stable' | 'warning-right' | 'danger-right';

const ZONE_STATUS: Record<ZoneId, { label: string; dotColor: string; textColor: string }> = {
  'danger-left':   { label: 'Danger',  dotColor: '#ef4444', textColor: '#dc2626' },
  'warning-left':  { label: 'Warning', dotColor: '#f59e0b', textColor: '#d97706' },
  'stable':        { label: 'Stable',  dotColor: '#10b981', textColor: '#059669' },
  'warning-right': { label: 'Warning', dotColor: '#f59e0b', textColor: '#d97706' },
  'danger-right':  { label: 'Danger',  dotColor: '#ef4444', textColor: '#dc2626' },
};

function getZoneAndPosition(
  value: number,
  riskTolerance: IRiskTolerance,
): { zoneId: ZoneId; percent: number } {
  const { dangerMinus, warnMinus, warnPlus, dangerPlus } = riskTolerance;

  if (value <= dangerMinus) {
    return { zoneId: 'danger-left', percent: 2 };
  }
  if (value < warnMinus) {
    const range = warnMinus - dangerMinus;
    const t = range > 0 ? (value - dangerMinus) / range : 0.5;
    return { zoneId: 'warning-left', percent: ZONE_WIDTH + t * ZONE_WIDTH };
  }
  if (value <= warnPlus) {
    const range = warnPlus - warnMinus;
    const t = range > 0 ? (value - warnMinus) / range : 0.5;
    return { zoneId: 'stable', percent: 2 * ZONE_WIDTH + t * ZONE_WIDTH };
  }
  if (value < dangerPlus) {
    const range = dangerPlus - warnPlus;
    const t = range > 0 ? (value - warnPlus) / range : 0.5;
    return { zoneId: 'warning-right', percent: 3 * ZONE_WIDTH + t * ZONE_WIDTH };
  }
  return { zoneId: 'danger-right', percent: 98 };
}

export const LinearGauge: React.FC<LinearGaugeProps> = ({
  currentValue,
  riskTolerance,
}) => {
  const { dangerMinus, warnMinus, warnPlus, dangerPlus } = riskTolerance;

  const isValidRange =
    dangerMinus < warnMinus &&
    warnMinus < warnPlus &&
    warnPlus < dangerPlus;

  const { zoneId, percent } = useMemo(
    () => isValidRange ? getZoneAndPosition(currentValue, riskTolerance) : { zoneId: 'stable' as ZoneId, percent: 50 },
    [currentValue, riskTolerance, isValidRange],
  );

  const status = ZONE_STATUS[zoneId];
  const formattedValue = FormatToShortPriceTemplate(currentValue, '', 0);

  if (!isValidRange) {
    return (
      <div className="flex w-full h-6 bg-gray-100 rounded-full items-center justify-center text-xs text-gray-400">
        No risk range configured
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 w-full">
      {/* Gauge bar */}
      <div className="relative w-full">
        {/* Cursor label — floating above */}
        <div
          className="absolute -top-7 z-10 flex flex-col items-center"
          style={{ left: `${percent}%`, transform: 'translateX(-50%)' }}
        >
          <span
            className="text-xs font-bold whitespace-nowrap px-2 py-0.5 rounded-full"
            style={{ color: status.textColor, backgroundColor: `${status.dotColor}18` }}
          >
            {formattedValue}
          </span>
        </div>

        {/* The pill bar */}
        <div className="relative h-3 w-full rounded-full overflow-hidden flex">
          {ZONES.map((zone) => {
            const isActive = zone.id === zoneId;
            return (
              <div
                key={zone.id}
                className="h-full transition-colors duration-200"
                style={{
                  width: `${ZONE_WIDTH}%`,
                  backgroundColor: isActive ? zone.activeColor : zone.color,
                }}
              />
            );
          })}
        </div>

        {/* Cursor dot */}
        <div
          className="absolute top-0 h-3 z-10 flex items-center"
          style={{ left: `${percent}%`, transform: 'translateX(-50%)' }}
        >
          <div
            className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: status.dotColor }}
          />
        </div>
      </div>

      {/* Zone boundary labels */}
      <div className="relative w-full h-4">
        {/* Alarm thresholds (warning → danger) */}
        <span className="absolute text-[10px] text-gray-400" style={{ left: '20%', transform: 'translateX(-50%)' }}>
          {FormatToShortPriceTemplate(dangerMinus, '', 0)}
        </span>
        <span className="absolute text-[10px] text-gray-400" style={{ left: '40%', transform: 'translateX(-50%)' }}>
          {FormatToShortPriceTemplate(warnMinus, '', 0)}
        </span>
        <span className="absolute text-[10px] text-gray-400" style={{ left: '50%', transform: 'translateX(-50%)' }}>
          0
        </span>
        <span className="absolute text-[10px] text-gray-400" style={{ left: '60%', transform: 'translateX(-50%)' }}>
          {FormatToShortPriceTemplate(warnPlus, '', 0)}
        </span>
        <span className="absolute text-[10px] text-gray-400" style={{ left: '80%', transform: 'translateX(-50%)' }}>
          {FormatToShortPriceTemplate(dangerPlus, '', 0)}
        </span>
      </div>
    </div>
  );
};
